import { useCallback, useEffect, useMemo, useRef } from "react"
import type { SceneAsset } from "@/types"
import type { ClipLayout, ScriptContent } from "./constants"
import { PX_PER_SECOND, computeClipLayouts, generateWaveformBars } from "./constants"
import TimelineRuler from "./timeline-ruler"
import TimelinePlayhead from "./timeline-playhead"
import TimelineResizeHandle from "./timeline-resize-handle"
import TimelineTrackImage from "./timeline-track-image"
import TimelineTrackText from "./timeline-track-text"
import TimelineTrackAudio from "./timeline-track-audio"

export default function Timeline({
  content,
  imageUrls,
  brandColour,
  sceneAssets,
  sceneAssetStatuses,
  selectedIndex,
  onSelect,
  audioCurrentTime,
  isPlaying,
  hasVoiceover,
  hasMusic = false,
  musicVolume = 0.3,
  onMusicVolumeChange,
  voiceoverVolume = 1.0,
  onVoiceoverVolumeChange,
  onResizeClip,
}: {
  content: ScriptContent | undefined
  imageUrls: string[]
  brandColour: string
  sceneAssets: SceneAsset[]
  sceneAssetStatuses: Record<string, { status: SceneAsset["status"]; message?: string; progress?: number }>
  selectedIndex: number
  onSelect: (index: number) => void
  audioCurrentTime: number
  isPlaying: boolean
  hasVoiceover: boolean
  hasMusic?: boolean
  musicVolume?: number
  onMusicVolumeChange?: (volume: number) => void
  voiceoverVolume?: number
  onVoiceoverVolumeChange?: (volume: number) => void
  onResizeClip: (leftIndex: number, leftType: string, delta: number) => void
}) {
  const pxPerSec = PX_PER_SECOND
  const clips = computeClipLayouts(content, pxPerSec)
  const totalDuration = clips.length > 0
    ? clips[clips.length - 1].startTime + clips[clips.length - 1].duration
    : 0
  const totalWidth = totalDuration * pxPerSec

  // Build resize handle data between adjacent clips
  const handlePairs: Array<{ left: ClipLayout; right: ClipLayout; position: number }> = []
  for (let i = 0; i < clips.length - 1; i++) {
    handlePairs.push({
      left: clips[i],
      right: clips[i + 1],
      position: clips[i].left + clips[i].width,
    })
  }

  const handleResize = useCallback(
    (leftClip: ClipLayout, delta: number) => {
      onResizeClip(leftClip.index, leftClip.type, delta)
    },
    [onResizeClip],
  )

  // Auto-scroll timeline to follow playhead during playback
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!isPlaying || !scrollRef.current) return
    const el = scrollRef.current
    const labelW = 56
    const pad = 4
    const playheadX = audioCurrentTime * pxPerSec
    const visibleW = el.clientWidth - labelW
    const playheadInView = playheadX + pad - el.scrollLeft

    if (playheadInView > visibleW * 0.85 || playheadInView < 0) {
      el.scrollLeft = Math.max(0, playheadX + pad - visibleW * 0.25)
    }
  }, [isPlaying, audioCurrentTime, pxPerSec])

  return (
    <div className="overflow-hidden rounded-lg border border-stone/30 bg-white">
      <div ref={scrollRef} className="overflow-x-auto overflow-y-hidden">
        <div className="flex" style={{ minWidth: totalWidth + 56 + 4 }}>
          {/* Track labels column */}
          <div className="sticky left-0 z-10 w-14 shrink-0 border-r border-stone/20 bg-white">
            {/* Ruler spacer */}
            <div className="flex h-5 items-center justify-center border-b border-stone/20">
              <span className="text-[9px] text-warm-grey/50">Time</span>
            </div>
            {/* Image track label */}
            <div className="flex h-14 items-center justify-center border-b border-stone/10">
              <svg className="size-3.5 text-warm-grey/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
              </svg>
            </div>
            {/* Text track label */}
            <div className="flex h-8 items-center justify-center border-b border-stone/10">
              <span className="text-[10px] font-medium text-warm-grey/50">Aa</span>
            </div>
            {/* Voiceover track label */}
            <div className="flex h-10 items-center justify-center border-b border-stone/10">
              <svg className="size-3.5 text-warm-grey/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
              </svg>
            </div>
            {/* Music track label */}
            <div className="flex h-8 items-center justify-center">
              <svg className="size-3.5 text-warm-grey/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m9 9 10.5-3m0 6.553v3.75a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 1 1-.99-3.467l2.31-.66a2.25 2.25 0 0 0 1.632-2.163Zm0 0V4.846a2.25 2.25 0 0 0-1.632-2.163l-6.368-1.82A2.25 2.25 0 0 0 7.5 3.025V13.5m0 0a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 0 1-.99-3.467l2.31-.66A2.25 2.25 0 0 0 7.5 9.75v3.75Z" />
              </svg>
            </div>
          </div>

          {/* Timeline content area */}
          <div className="relative min-w-0 flex-1 pl-1">
            {/* Ruler */}
            <TimelineRuler totalDuration={totalDuration} pxPerSec={pxPerSec} />

            {/* Image track */}
            <div className="relative border-b border-stone/10">
              <TimelineTrackImage
                clips={clips}
                imageUrls={imageUrls}
                content={content}
                brandColour={brandColour}
                sceneAssets={sceneAssets}
                sceneAssetStatuses={sceneAssetStatuses}
                selectedIndex={selectedIndex}
                onSelect={onSelect}
              />
            </div>

            {/* Text track */}
            <div className="relative border-b border-stone/10">
              <TimelineTrackText
                clips={clips}
                content={content}
                selectedIndex={selectedIndex}
                onSelect={onSelect}
              />
            </div>

            {/* Voiceover track */}
            <div className="group/voiceover relative border-b border-stone/10">
              {hasVoiceover ? (
                <>
                  <TimelineTrackAudio
                    clips={clips}
                    totalDuration={totalDuration}
                    pxPerSec={pxPerSec}
                    audioCurrentTime={audioCurrentTime}
                    isPlaying={isPlaying}
                  />
                  {/* Volume control — shown on hover */}
                  {onVoiceoverVolumeChange && (
                    <div className="absolute left-1 top-1/2 flex -translate-y-1/2 items-center gap-1.5 rounded bg-white/95 px-1.5 py-0.5 opacity-0 shadow-sm ring-1 ring-stone/20 transition-opacity group-hover/voiceover:opacity-100">
                      <svg className="size-3 shrink-0 text-red/70" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
                      </svg>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={voiceoverVolume}
                        onChange={(e) => onVoiceoverVolumeChange(parseFloat(e.target.value))}
                        className="h-1 w-16 cursor-pointer accent-red"
                      />
                      <span className="w-6 text-right text-[9px] tabular-nums text-warm-grey">
                        {Math.round(voiceoverVolume * 100)}%
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex h-10 items-center justify-center" style={{ width: totalWidth }}>
                  <span className="text-[10px] text-warm-grey/50">No voiceover</span>
                </div>
              )}
            </div>

            {/* Music track */}
            <div className="relative">
              <TimelineTrackMusic
                totalWidth={totalWidth}
                totalDuration={totalDuration}
                hasMusic={hasMusic}
                musicVolume={musicVolume}
                onMusicVolumeChange={onMusicVolumeChange}
              />
            </div>

            {/* Resize handles (spans all tracks) */}
            {handlePairs.map(({ left, right, position }) => (
              <div
                key={`handle-${left.index}-${left.type}`}
                className="absolute top-5 bottom-0"
                style={{ left: position }}
              >
                <TimelineResizeHandle
                  pxPerSec={pxPerSec}
                  leftDuration={left.duration}
                  rightDuration={right.duration}
                  onResize={(delta) => handleResize(left, delta)}
                />
              </div>
            ))}

            {/* Playhead */}
            <div className="absolute top-0 bottom-0">
              <TimelinePlayhead
                currentTime={audioCurrentTime}
                pxPerSec={pxPerSec}
                visible={isPlaying}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function TimelineTrackMusic({
  totalWidth,
  totalDuration,
  hasMusic,
  musicVolume = 0.3,
  onMusicVolumeChange,
}: {
  totalWidth: number
  totalDuration: number
  hasMusic: boolean
  musicVolume?: number
  onMusicVolumeChange?: (volume: number) => void
}) {
  const barsPerSecond = 3
  const totalBars = Math.max(1, Math.ceil(totalDuration * barsPerSecond))
  const musicBars = useMemo(
    () => generateWaveformBars(777, totalBars),
    [totalBars],
  )

  if (!hasMusic) {
    return (
      <div className="flex h-8 items-center justify-center" style={{ width: totalWidth }}>
        <span className="text-[10px] text-warm-grey/50">No music</span>
      </div>
    )
  }

  return (
    <div className="group/music relative h-8" style={{ width: totalWidth }}>
      {/* Waveform bars */}
      <div className="flex h-full items-end gap-px px-0.5 pb-0.5 pt-0.5">
        {musicBars.map((h, idx) => (
          <div
            key={idx}
            className="min-w-[2px] flex-1 rounded-sm bg-amber-500/25"
            style={{ height: `${h * musicVolume * 120}%` }}
          />
        ))}
      </div>

      {/* Volume control — shown on hover */}
      {onMusicVolumeChange && (
        <div className="absolute left-1 top-1/2 flex -translate-y-1/2 items-center gap-1.5 rounded bg-white/95 px-1.5 py-0.5 opacity-0 shadow-sm ring-1 ring-stone/20 transition-opacity group-hover/music:opacity-100">
          <svg className="size-3 shrink-0 text-amber-600/70" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
          </svg>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={musicVolume}
            onChange={(e) => onMusicVolumeChange(parseFloat(e.target.value))}
            className="h-1 w-16 cursor-pointer accent-amber-500"
          />
          <span className="w-6 text-right text-[9px] tabular-nums text-warm-grey">
            {Math.round(musicVolume * 100)}%
          </span>
        </div>
      )}
    </div>
  )
}
